import { Grid } from '@material-ui/core';
import CardTitle from 'app/components/CardTitle';
import BaseLine from 'app/components/Charts/BaseLine';
import DataLabelChart from 'app/components/Charts/DataLabel';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { getThrough } from 'app/utils/getThrough';
import { checkTranslated } from 'app/utils/helpers';
import { _t } from 'app/utils/i18n';
import { DonutChartData } from 'app/utils/types';
import React, { FC, Fragment } from 'react';
import AddComment from 'reports/components/AddComment';
import VideoSlider from 'trends/components/VideoSlider';
import styles from './CompareVideos.module.scss';
import { apiRoutes } from 'videos/utils/apiRoutes';
import { useReport } from 'reports/pages/AddNewReport/AddNewReport';
import Paragraph from 'reports/components/Paragraph';

const videosCharts = [
  {
    title: _t('trends:compare_videos_views'),
    key: 'views_count',
    tooltipTitle: 'videos:view',
    id: 'compare_videos_views',
  },
  {
    title: _t('trends:compare_comments'),
    key: 'comments_count',
    tooltipTitle: 'videos:comment',
    id: 'compare_comments',
  },
  {
    title: _t('trends:compare_likes'),
    key: 'likes_count',
    tooltipTitle: 'videos:like',
    id: 'compare_likes',
  },
  // {
  //   title: _t('trends:compare_dislikes'),
  //   key: 'dislikes_count',
  //   tooltipTitle: 'videos:dislike',
  //   id: 'compareDisLikes',
  // },
  {
    title: _t('trends:video_engagement'),
    key: 'total_engagements',
    tooltipTitle: 'videos:engagement',
    id: 'video_engagement',
  },
];

const baseLineMap = () => [
  {
    title: _t('trends:gained_subs'),
    slugName: apiRoutes().channelSubscribers,
    id: 'channel_gained_subscribers',
  },

  {
    title: _t('trends:channel_views'),
    slugName: apiRoutes().channelViews,
    id: 'channel_views',
  },
];

const channelCharts = [
  // {
  //   title: _t('videos:channel_activity'),
  //   key: 'channel.activity',
  //   tooltipTitle: 'activity',
  //   id: 'channelActivities',
  // },
  {
    title: _t('videos:uploaded_videos'),
    key: 'channel.uploaded_count',
    tooltipTitle: 'videos',
    id: 'uploaded_videos',
  },
  {
    title: _t('trends:channel_total_views'),
    key: 'channel.views_count',
    tooltipTitle: 'views',
    id: 'channel_total_views',
  },
  {
    title: _t('videos:channel_total_subs'),
    key: 'channel.subscriber_count',
    tooltipTitle: 'views',
    id: 'channel_total_subscribers',
  },
];

type Props = {
  videosIds: number[];
  videos: any;
  isReport?: boolean;
  isTemplate?: boolean;
  selectedSlugs?: any;
};
const VideoCompareCharts: FC<Props> = ({
  videosIds,
  videos,
  isReport,
  isTemplate,
  selectedSlugs,
}) => {
  const checkIsReport = (report: any, notReport: any = undefined) =>
    isReport ? (isTemplate ? notReport : report) : notReport;
  const chartData = (
    // TODO: move this method to another file and make it more generic
    keyExtractor: string,
    type: 'video' | 'channel' = 'video'
  ): DonutChartData => {
    const data: { labels: string[]; series: number[]; date: string[] } = {
      labels: [],
      series: [],
      date: [],
    };
    videos?.videos?.forEach((video: any) => {
      data.labels.push(
        type == 'video'
          ? checkTranslated(video?.title, video?.title_translated)
          : checkTranslated(
              video?.channel?.name,
              video?.channel?.name_translated
            )
      );
      data.series.push(getThrough(keyExtractor, video));
      data.date.push(
        type == 'video' ? video.published_at : video?.channel?.joined_at
      );
    });
    return {
      ...data,
      series: data.series.filter((el) => typeof el === 'number'),
    };
  };
  const report = useReport();

  const charts = [
    {
      id: 'videos_compare_videos_video_details',
      component: (
        <>
          <Grid key={'videos_compare_videos_video_details'} item sm={12}>
            <ErrorBoundary>
              <CardTitle
                title={_t('trends:video_details')}
                showBorderBottom
                hideIcon
                maxFontSize
                compId="videos_compare_videos_video_details"
              />
            </ErrorBoundary>
          </Grid>
        </>
      ),
    },
    ...videosCharts.map(({ key, title, id, tooltipTitle }) => ({
      id: `videos_compare_videos_${id}`,
      component: (
        <>
          <Grid key={key} item md={checkIsReport(6, 4)} sm={12}>
            <ErrorBoundary>
              <DataLabelChart
                showLegend
                data={chartData(key)}
                isReport={isReport}
                tooltipTitle={tooltipTitle}
                compId={`videos_compare_videos_${id}`}
                dataLabelTitle={title}
                additionalComponents={checkIsReport(
                  <AddComment compId={`videos_compare_videos_${id}`} />
                )}
              />
            </ErrorBoundary>
          </Grid>
        </>
      ),
    })),
    {
      id: 'videos_compare_videos_channels_details',
      component: (
        <>
          <Grid
            key="videos_compare_videos_channels_details"
            item
            sm={12}
            className={styles.mb}
          >
            <ErrorBoundary>
              <CardTitle
                title={_t('trends:channel_details')}
                showBorderBottom
                hideIcon
                maxFontSize
                compId="videos_compare_videos_channels_details"
              />
            </ErrorBoundary>
          </Grid>
        </>
      ),
    },
    ...channelCharts.map(({ key, title, id }) => ({
      id: `videos_compare_videos_${id}`,
      component: (
        <>
          <Grid key={key} item md={checkIsReport(6, 4)} sm={12}>
            <ErrorBoundary>
              <DataLabelChart
                showLegend
                data={chartData(key, 'channel')}
                channelTooltip
                uploadedVideosTooltip={id}
                //  tooltipTitle={tooltipTitle}
                compId={`videos_compare_videos_${id}`}
                dataLabelTitle={title}
                isReport={isReport}
                additionalComponents={checkIsReport(
                  <AddComment compId={`videos_compare_videos_${id}`} />
                )}
              />
            </ErrorBoundary>
          </Grid>
        </>
      ),
    })),

    ...baseLineMap().map(({ title, slugName, id }) => ({
      id: `videos_compare_videos_${id}`,
      component: (
        <>
          <Grid item md={6} sm={12} key={id}>
            <ErrorBoundary>
              <BaseLine
                baseLineTitle={title}
                customFilters={{ video_ids: videosIds }}
                timeIntervalFilter
                slugName={slugName}
                compId={`videos_compare_videos_${id}`}
                isReport={isReport}
                additionalComponents={checkIsReport(
                  <AddComment compId={`videos_compare_videos_${id}`} />
                )}
                noDayInTimeInterval
                showCustomTooltip
                compareVideos
              />
            </ErrorBoundary>
          </Grid>
        </>
      ),
    })),
    {
      id: 'video-slider',
      component: (
        <>
          <Grid key={'video-slider'} item sm={12}>
            <ErrorBoundary>
              <VideoSlider
                isReport={isReport || false}
                videos={videos?.videos}
              />
            </ErrorBoundary>
          </Grid>
        </>
      ),
    },
  ];
  const renderPage = () => {
    if (isReport) {
      const itemsSlugs = report?.values?.slugs || selectedSlugs;
      const chartsFilter = charts
        ?.filter((item) => itemsSlugs?.some((i: any) => i?.slug === item?.id))
        .map((item) => ({
          ...item,
          order: itemsSlugs.filter((i: any) => i?.slug === item?.id)?.[0]
            ?.order,
        }));

      const paragraph = itemsSlugs
        ?.map((item: any) =>
          !!item.paragraph
            ? {
                id: item.paragraph,
                order: item.order,
                component: <Paragraph title={item.paragraph} />,
              }
            : false
        )
        .filter(Boolean);

      return [...chartsFilter, ...paragraph]
        .sort((a, b) => a.order - b.order)
        .map((item) => item.component);
    }

    return charts.map((chart, index) => (
      <Fragment key={index}>{chart.component}</Fragment>
    ));
  };
  return <>{renderPage()}</>;
};

export default VideoCompareCharts;
